import React from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { Drawer, Tag, Button, Row, Col, Typography, Table, Spin, Divider, Space, Tooltip, Card, Collapse, Empty, Alert } from 'antd';
import { BankOutlined, CalendarOutlined, CreditCardOutlined, EditOutlined, FileTextOutlined, QuestionCircleOutlined, SwapOutlined, UndoOutlined, UserOutlined } from '@ant-design/icons';
import { checkPermission } from '@/utils/permission';
import NumberFormat from 'react-number-format';
import ReactHtmlParser from 'react-html-parser';
import { isMobile } from 'react-device-detect';
import ReceiptForm from './ReceiptForm';
import { renderReceiptStatus, renderPaymentMethod, renderReceiptPaymentType, ConvertReceiptType, isValidDate, renderMoney } from '@/utils/helpers';
import {
    getDepositStatusLabel,
    getDepositTransactionLabel,
    getReceiptCollectionPurposeLabel,
    getReceiptCurrentContent,
    formatDepositDisplayText,
} from '@/utils/receiptCollectionResult';
import PrintButton from '@/components/PrintButton/PrintButton';
import PrintTable from './PrintTable';
import CustomerServiceConvertForm from '@/components/customer-services/CustomerServiceConvertForm';
import { getServiceConversionEligibility } from '@/components/customer-services/customerServiceConversionPresentation';
import { getSpecificService } from '@/redux/actions/customerServicesActions';
import CreatedAtCell from '@/components/CreatedAtCell';
import ImagePreviewList from '@/components/ImagePreviewList';
import moment from 'moment';
import CustomerRefundModal from '@/components/customers/CustomerRefundModal';
import CustomerDepositActions from '@/components/customers/CustomerDepositActions';
import ReceiptHistoryDrawer, { ReceiptHistoryButton } from './ReceiptHistoryDrawer';
import { getCustomerDepositSummary, getReceiptHistory } from '@/redux/actions/receiptActions';
import {
    buildReceiptAllocationRows,
    buildReceiptContentRows,
    buildReceiptContentSummary,
    formatConvertedService,
} from './receiptDetailHelpers';
import ReceiptItemInfoCell from './ReceiptItemInfoCell';

const { Paragraph } = Typography;
const { Panel } = Collapse;

const labelStyle = {
    fontSize: 12,
    color: '#8c8c8c',
    marginRight: 4
};

const valueStyle = {
    fontWeight: 500,
    fontSize: 13,
    color: '#262626'
};

const totalStyle = {
    fontWeight: 600,
    fontSize: 14,
    color: '#1677ff'
};
class ReceiptDetail extends React.PureComponent {
    constructor() {
        super();

        this.state = {
            isOpenForm: false,
            isOpenStatusForm: false,
            isOpenConvertForm: false,
            currentService: null,

            // print lazy
            shouldRenderPrint: false,
            isOpenRefund: false,
            isOpenHistory: false,
            historyLoading: false,
            historyItems: [],
            isOpenDepositUpdate: false,
            depositUpdateLoading: false,
            depositUpdateData: null,
            depositToUpdate: null,
        };

        // Cache columns, không tạo lại trong render
        this.contentColumns = this.buildContentColumns();
        this.columnsTransfer = this.buildColumnsTransfer();
        this.financialTimelineColumns = this.buildFinancialTimelineColumns();
    }
    handleCancel = () => {
        this.props.onCancel();

        // reset lazy flags để lần mở sau nhẹ
        this.setState({
            shouldRenderPrint: false,
            showAllImages: false,
        });
    };

    toggleOpenForm = isOpenForm => {
        this.setState({ isOpenForm });
    };

    toggleOpenUpdateStatusForm = isOpenStatusForm => {
        this.setState({ isOpenStatusForm });
    };

    loadData = () => {
        return this.props.reloadData();
    };

    openDepositUpdate = () => {
        const receipt = this.props.currentReceipt;
        if (!receipt?.id || !receipt?.customer_id || this.state.depositUpdateLoading) return;
        this.setState({ depositUpdateLoading: true });
        this.props.getCustomerDepositSummary(receipt.customer_id).then(data => {
            const depositInfo = receipt.deposit_info || {};
            const activeDeposit = (data.active_deposits || [])
                .find(item => Number(item.source_receipt_id) === Number(receipt.id));
            const currentTargets = depositInfo.current_target_services || depositInfo.target_services || [];
            const depositToUpdate = {
                ...(activeDeposit || {}),
                id: Number(activeDeposit?.id || depositInfo.deposit_id),
                source_receipt_id: Number(receipt.id),
                original_amount: Number(receipt.amount_paid || depositInfo.received_amount || 0),
                current_balance: Number(depositInfo.current_balance || 0),
                status: depositInfo.status,
                received_at: activeDeposit?.received_at || depositInfo.received_at || receipt.payment_at,
                branch_id: Number(activeDeposit?.branch_id || receipt.branch_id || 0),
                payment_method: receipt.payment_method,
                payment_breakdowns: receipt.payment_breakdowns || receipt.paymentBreakdowns || [],
                target_customer_service_ids: currentTargets.map(item => Number(item.id)).filter(Boolean),
                receipt_note: receipt.note || '',
                commission_revision: Number(receipt.commission_revision || 1),
                financial_update_locked: !!depositInfo.financial_update_locked,
            };
            this.setState({
                isOpenDepositUpdate: true,
                depositUpdateData: data,
                depositToUpdate,
            });
        }).catch(() => null).finally(() => this.setState({ depositUpdateLoading: false }));
    };

    closeDepositUpdate = () => {
        this.setState({
            isOpenDepositUpdate: false,
            depositUpdateData: null,
            depositToUpdate: null,
        });
    };

    openHistory = () => {
        const receiptId = this.props.currentReceipt?.id;
        if (!receiptId) return;
        this.setState({ isOpenHistory: true, historyLoading: true });
        this.props
            .getReceiptHistory(receiptId)
            .then(historyItems => this.setState({ historyItems }))
            .catch(() => this.setState({ historyItems: [] }))
            .finally(() => this.setState({ historyLoading: false }));
    };

    toggleOpenConvertForm = (isOpenConvertForm, currentService = null) => {
        this.setState({
            isOpenConvertForm,
            currentService,
        });
    };

    onConvert = id => {
        this.props.getSpecificService(id).then(res => {
            this.toggleOpenConvertForm(true, res);
        });
    };

    buildCustomer = currentReceipt => {
        return {
            customer_name: currentReceipt?.customer?.full_name || '',
            customer_phone: Array.isArray(currentReceipt?.customer?.phones)
                ? currentReceipt.customer.phones
                    .map(item => item?.phone)
                    .filter(Boolean)
                    .join(', ')
                : '',
            customer_address: currentReceipt?.customer?.address || '',
            province_name: currentReceipt?.customer?.province?.name || '',
            wards_name: currentReceipt?.customer?.ward?.name || '',
            invoice_no: currentReceipt?.code || '',
            special_discount: currentReceipt?.special_discount ?? 0,
            amount_paid: currentReceipt?.amount_paid ?? 0,
            remaining_debt: currentReceipt?.remaining_debt ?? 0,
            total_amount_paid: currentReceipt?.total_amount_paid ?? 0,
            collection_purpose: currentReceipt?.collection_purpose,
            deposit_info: currentReceipt?.deposit_info,
            settlement_summary: currentReceipt?.settlement_summary,
        };
    };

    rowKeyItems = (record, index) => {
        return (
            record?.id ||
            record?.customer_service_id ||
            `${record?.name || 'row'}_${index}`
        );
    };

    rowKeyTransferItems = (record, index) => {
        return (
            record?.id ||
            record?.code ||
            record?.customer_service_id ||
            `${record?.name || 'row'}_${index}`
        );
    };

    toggleOpenRefund = isOpenRefund => {
        this.setState({ isOpenRefund });
    };

    buildFinancialTimeline = (lineage = {}) => {
        const entryLabels = {
            RECEIPT_OVERPAYMENT: 'Tiền thu chưa phân bổ',
            APPLIED_TO_CUSTOMER_SERVICE: 'Dùng số dư cho dịch vụ',
            CUSTOMER_REFUND: 'Hoàn tiền cho khách',
            RECEIPT_BALANCE_REVERSAL: 'Điều chỉnh do sửa phiếu',
            RECEIPT_DELETE_REVERSAL: 'Hoàn nguồn do hủy phiếu',
            SERVICE_ADJUSTMENT_RELEASE: 'Số dư do điều chỉnh dịch vụ',
            SERVICE_CONVERSION_SURPLUS: 'Tiền thừa từ chuyển đổi dịch vụ',
        };
        const entries = (lineage.balance_entries || [])
            .filter(
                row =>
                    ![
                        'RECEIPT_OVERPAYMENT',
                        'APPLIED_TO_CUSTOMER_SERVICE',
                        'CUSTOMER_REFUND',
                    ].includes(row.entry_type)
            )
            .map(row => ({
                ...row,
                timeline_key: `entry-${row.id}`,
                event_at: row.effective_at,
                event_label: entryLabels[row.entry_type] || 'Điều chỉnh số dư',
                signed_amount:
                    row.direction === 'DEBIT'
                        ? -Number(row.amount || 0)
                        : Number(row.amount || 0),
            }));

        const applications = (lineage.balance_applications || []).map(row => ({
            ...row,
            timeline_key: `application-${row.id || row.application_id}-${row.source_balance_entry_id
                }`,
            event_at: row.applied_at,
            event_label: 'Đã dùng số dư cho dịch vụ',
            signed_amount: -Number(row.amount || 0),
        }));

        const refunds = (lineage.refund_sources || [])
            .filter(row => row.status === 'COMPLETED')
            .map(row => ({
                ...row,
                timeline_key: `refund-${row.id ||
                    row.refund_id}-${row.source_balance_entry_id ||
                    row.source_allocation_id}`,
                event_at: row.refund_at,
                event_label: 'Đã hoàn tiền cho khách',
                signed_amount: -Number(row.amount || 0),
            }));

        const deposits = (lineage.deposit_transactions || [])
            .filter(row => row.transaction_type !== 'DEPOSIT_RECEIVED')
            .map(row => ({
                ...row,
                timeline_key: `deposit-${row.id}`,
                event_at: row.effective_at,
                event_label:
                    getDepositTransactionLabel(row.transaction_type),
                signed_amount:
                    row.direction === 'DEBIT'
                        ? -Number(row.amount || 0)
                        : row.direction === 'CREDIT'
                            ? Number(row.amount || 0)
                            : 0,
            }));

        return [
            ...entries,
            ...applications,
            ...refunds,
            ...deposits,
        ].sort((a, b) =>
            String(b.event_at || '').localeCompare(String(a.event_at || ''))
        );
    };

    getRefundedAmount = (lineage = {}) => {
        return (lineage.refund_sources || [])
            .filter(row => row.status === 'COMPLETED')
            .reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    };

    buildAllocationRows = lineage => buildReceiptAllocationRows(lineage);

    getAllocationSourceTotal = (allocationRows, origin) =>
        allocationRows
            .filter(row => row.allocation_origin === origin)
            .reduce((sum, row) => sum + Number(row.remaining_amount || 0), 0);

    renderSectionHeader = (title, description = null, extra = null) => (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 8,
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 18,
                        lineHeight: '24px',
                        fontWeight: 700,
                        color: 'rgba(0, 0, 0, 0.88)',
                    }}
                >
                    {title}
                </div>

                {description && (
                    <div
                        style={{
                            marginTop: 1,
                            fontSize: 13,
                            lineHeight: '19px',
                            color: 'rgba(0, 0, 0, 0.45)',
                        }}
                    >
                        {description}
                    </div>
                )}
            </div>

            {extra}
        </div>
    );

    renderInfoField = (label, value, options = {}) => {
        const {
            strong = false,
            danger = false,
            compact = false,
            icon = null,
            primary = false,
            surface = false,
        } = options;

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: icon ? 9 : 0,
                    height: '100%',
                    minHeight: primary ? 74 : compact ? 44 : 56,
                    padding: primary ? '10px 12px' : surface ? '8px 10px' : compact ? '2px 0' : '4px 0',
                    border: primary
                        ? '1px solid #d9e8ff'
                        : surface
                            ? '1px solid #f0f0f0'
                            : 'none',
                    borderRadius: primary || surface ? 8 : 0,
                    background: primary ? '#f7fbff' : surface ? '#fafafa' : 'transparent',
                }}
            >
                {icon && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: '0 0 auto',
                            width: 30,
                            height: 30,
                            marginTop: 1,
                            borderRadius: 8,
                            color: primary ? '#1677ff' : '#595959',
                            background: primary ? '#e6f4ff' : '#f0f0f0',
                            fontSize: 15,
                        }}
                    >
                        {icon}
                    </div>
                )}

                <div
                    style={{
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <div
                        style={{
                            marginBottom: 1,
                            fontSize: 12,
                            lineHeight: '18px',
                            fontWeight: primary ? 600 : 400,
                            color: primary ? '#1677ff' : 'rgba(0, 0, 0, 0.45)',
                        }}
                    >
                        {label}
                    </div>

                    <div
                        style={{
                            fontSize: primary ? 18 : strong ? 16 : 14,
                            lineHeight: primary ? '26px' : strong ? '23px' : '21px',
                            fontWeight: strong || primary ? 700 : 600,
                            color: danger ? '#cf1322' : 'rgba(0, 0, 0, 0.88)',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {value}
                    </div>
                </div>
            </div>
        );
    };

    renderCommissionProcessingStatus = status => {
        if (status === 'COMPLETED') return <Tag color="green">Hoa hồng đã đồng bộ</Tag>;
        if (status === 'SKIPPED') return <Tag>Không xử lý hoa hồng</Tag>;
        if (['PENDING', 'RUNNING'].includes(status)) return <Tag color="blue">Hoa hồng đang xử lý</Tag>;
        if (status === 'WAITING') return <Tag color="orange">Hoa hồng chờ nghiệp vụ</Tag>;
        if (status === 'FAILED_RETRY') return <Tag color="volcano">Hoa hồng chờ thử lại</Tag>;
        return null;
    };

    renderFinancialMetric = (label, value, options = {}) => {
        const {
            primary = false,
            danger = false,
            positive = false,
            muted = false,
        } = options;

        let valueColor = 'rgba(0, 0, 0, 0.88)';

        if (danger) {
            valueColor = '#cf1322';
        } else if (positive) {
            valueColor = '#237804';
        } else if (muted) {
            valueColor = 'rgba(0, 0, 0, 0.65)';
        }

        return (
            <div
                style={{
                    height: '100%',
                    minHeight: primary ? 86 : 76,
                    padding: primary ? '10px 12px' : '8px 10px',
                    border: primary ? '1px solid #d9e8ff' : '1px solid #f0f0f0',
                    borderRadius: 8,
                    background: primary ? '#f7fbff' : '#fafafa',
                }}
            >
                <div
                    style={{
                        marginBottom: 3,
                        fontSize: 13,
                        lineHeight: '19px',
                        fontWeight: 500,
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        fontSize: primary ? 22 : 18,
                        lineHeight: primary ? '30px' : '26px',
                        fontWeight: 700,
                        color: valueColor,
                        wordBreak: 'break-word',
                    }}
                >
                    {value}
                </div>
            </div>
        );
    };

    renderMiniFinancialField = (label, value, options = {}) => {
        const { danger = false, strong = false } = options;

        return (
            <div
                style={{
                    padding: '6px 0',
                    minHeight: 52,
                }}
            >
                <div
                    style={{
                        marginBottom: 2,
                        fontSize: 13,
                        lineHeight: '19px',
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        fontSize: strong ? 18 : 16,
                        lineHeight: '24px',
                        fontWeight: 700,
                        color: danger ? '#cf1322' : 'rgba(0, 0, 0, 0.88)',
                    }}
                >
                    {value}
                </div>
            </div>
        );
    };

    buildContentColumns = () => [
        {
            title: 'STT',
            key: 'stt',
            width: 50,
            align: 'center',
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Tên dịch vụ/Sản phẩm',
            dataIndex: 'name',
            width: 290,
            render: (value, record) => {
                const convertedServices = (record.converted_to_services || [])
                    .map(formatConvertedService)
                    .filter(Boolean);
                const currentName = record?.current_service_financial?.name;
                const snapshotName = typeof value === 'string' ? value.trim() : '';

                return (
                    <ReceiptItemInfoCell
                        name={snapshotName || 'Nội dung chưa xác định'}
                        type={record.type}
                        serviceId={record.origin_id}
                        customerServiceId={record.customer_service_id}
                        selectedAt={record?.current_service_financial?.selected_at || record.selected_at}
                    >
                        {currentName && currentName.trim() !== snapshotName && (
                            <div style={{ color: '#1677ff', fontSize: 12 }}>
                                Hiện tại: {currentName}
                            </div>
                        )}
                        {convertedServices.map((service, index) => (
                            <div
                                key={`${record.id || record.customer_service_id}-converted-${index}`}
                                style={{
                                    marginTop: 2,
                                    fontSize: 12,
                                    lineHeight: '18px',
                                    fontWeight: 400,
                                    color: 'rgba(247, 0, 0, 0.55)',
                                }}
                            >
                                Đã chuyển sang: {service}
                            </div>
                        ))}
                    </ReceiptItemInfoCell>
                );
            },
        },
        {
            title: 'Giá & số lượng',
            key: 'price_quantity',
            width: 180,
            align: 'right',
            render: (_, record) => {
                const { price, quantity } = record;

                if (price == null || quantity == null) return '—';

                const salePrice = Number(price);
                const qty = Number(quantity);
                const amount = salePrice * qty;

                const current = record.current_service_financial;
                const currentQty = current?.quantity;
                const currentPrice = current?.sale_price;
                const currentAmount = current?.amount;

                const qtyChanged =
                    currentQty != null && Number(currentQty) !== qty;

                const priceChanged =
                    currentPrice != null && Number(currentPrice) !== salePrice;

                const amountChanged =
                    currentAmount != null && Number(currentAmount) !== amount;

                return (
                    <div style={{ textAlign: 'right', lineHeight: 1.6 }}>

                        {/* Giá bán × Số lượng */}
                        <div style={{ fontSize: 12 }}>
                            <span style={labelStyle}>Giá bán:</span>
                            <span style={valueStyle}>
                                {renderMoney(salePrice)} × {qty}
                            </span>
                        </div>
                        {/* Thành tiền */}
                        <div style={totalStyle}>
                            <span style={labelStyle}>Thành tiền:</span>
                            {renderMoney(amount)}
                        </div>

                        {/* Giá bán hoặc số lượng hiện tại thay đổi */}
                        {(priceChanged || qtyChanged) && (
                            <div style={{ fontSize: 12, color: '#1677ff' }}>
                                Hiện tại:{' '}
                                {renderMoney(
                                    priceChanged ? Number(currentPrice) : salePrice
                                )}{' '}
                                × {qtyChanged ? Number(currentQty) : qty}
                            </div>
                        )}

                        {/* Thành tiền hiện tại thay đổi */}
                        {amountChanged && (
                            <div style={{ fontSize: 12, color: '#1677ff' }}>
                                Thành tiền hiện tại: {renderMoney(Number(currentAmount))}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="Tỷ trọng = Nợ trước phiếu của dịch vụ ÷ Tổng nợ trước phiếu của các dịch vụ được chọn × 100%. Dùng để xác định phần tiền mỗi dịch vụ được nhận.">
                    <span>
                        Tỷ trọng{' '}
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </span>
                </Tooltip>
            ),
            dataIndex: 'allocation_ratio_percent',
            width: 80,
            align: 'right',
            render: value =>
                value === null || value === undefined ? (
                    '—'
                ) : (
                    <strong>
                        {Number(value || 0).toLocaleString('vi-VN', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4,
                        })}%
                    </strong>
                ),
        },
        {
            title: (
                <Tooltip title="Số tiền thanh toán dành cho dịch vụ của phiếu này × Tỷ trọng. Bao gồm tiền thực nộp và tiền cọc/số dư đã dùng. Không cộng các phiếu khác.">
                    <span>
                        Đã phân bổ{' '}
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </span>
                </Tooltip>
            ),
            dataIndex: 'receipt_paid_allocated_amount',
            width: 180,
            align: 'right',
            render: (value, record) => {
                const totalAtReceipt = Math.max(0, Number(value || 0));
                const effectiveAmount = Math.max(0, Number(record.receipt_paid_effective_amount || 0));
                return (
                    <Tooltip
                        placement="topRight"
                        title={
                            <div style={{ maxWidth: 340 }}>
                                {/* <div style={{ marginBottom: 8 }}>
                                    <div>Tổng tại phiếu: {renderMoney(totalAtReceipt)}</div>
                                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                                        Tổng tiền của mọi nguồn thanh toán đã được gắn vào dịch vụ tại phiên bản còn hiệu lực của phiếu.
                                    </div>
                                </div> */}
                                <div>
                                    <div>Còn hiệu lực: {renderMoney(effectiveAmount)}</div>
                                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                                        Phần phân bổ cuối cùng vẫn đang giảm công nợ sau hoàn tiền hoặc điều chỉnh.
                                    </div>
                                </div>
                                {record.receipt_paid_refunded_amount > 0 && (
                                    <div style={{ color: '#ff7875', marginTop: 8 }}>
                                        Hoàn/điều chỉnh {renderMoney(record.receipt_paid_refunded_amount)}
                                    </div>
                                )}
                            </div>
                        }
                    >
                        <span style={{ ...valueStyle, display: 'inline-block', fontWeight: 600, cursor: 'help' }}>
                            {renderMoney(totalAtReceipt)}{' '}
                            <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: (
                <Tooltip title="Chi tiết công nợ của dịch vụ trước và sau khi ghi nhận phiếu thu này.">
                    <span>
                        Công nợ tại phiếu{' '}
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </span>
                </Tooltip>
            ),

            key: 'receipt_debt_summary',
            width: 180,
            align: 'right',
            render: (_, record) => {
                const debtBefore = record.debt_before_amount;
                const debtReduced = record.debt_reduced_amount;
                const debtAfter = record.debt_after_amount;
                const formatAmount = value =>
                    value == null ? '—' : renderMoney(Number(value));
                const rows = [
                    {
                        label: 'Nợ trước:',
                        value: debtBefore,
                        tooltip: 'Công nợ còn lại của dịch vụ trước khi áp dụng phiếu thu này.',
                    },
                    {
                        label: 'Giảm nợ:',
                        value: debtReduced,
                        tooltip: 'Số tiền thực tế được khấu trừ vào công nợ dịch vụ tại phiếu này.',
                    },
                    {
                        label: 'Còn nợ:',
                        value: debtAfter,
                        tooltip: 'Công nợ trước phiếu trừ số tiền giảm nợ tại phiếu.',
                    },
                ];
                return (
                    <div style={{ textAlign: 'right', lineHeight: 1.6 }}>
                        {debtBefore != null && debtReduced != null && (
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                {formatAmount(debtBefore)} − {formatAmount(debtReduced)}
                            </div>
                        )}
                        <Tooltip
                            placement="topRight"
                            title={
                                <div style={{ maxWidth: 350 }}>
                                    {rows.map((item, index) => (
                                        <div key={index} style={{ marginBottom: index === rows.length - 1 ? 0 : 8 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                <span>{item.label}</span>
                                                <strong>{formatAmount(item.value)}</strong>
                                            </div>
                                            <div style={{ fontSize: 12, opacity: 0.85 }}>
                                                {item.tooltip}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            }
                        >
                            <span
                                style={{
                                    ...valueStyle,
                                    display: 'inline-block',
                                    fontWeight: 600,
                                    color: debtAfter > 0 ? '#cf1322' : undefined,
                                    cursor: 'help',
                                }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 400, color: '#8c8c8c' }}>Còn nợ: </span>
                                {formatAmount(debtAfter)}{' '}
                                <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                            </span>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            title: 'Action',
            align: 'center',
            width: 110,
            key: 'actions',
            fixed: 'right',
            render: (_, record) => {
                if (!record.customer_service_id || record.type === 'Sản phẩm') {
                    return null;
                }

                const conversionEligibility = getServiceConversionEligibility(record);
                const isConversionDisabled = !conversionEligibility.canConvert;
                return (
                    checkPermission(
                        'customer_services',
                        'convert_to_service'
                    ) && (
                        <Tooltip
                            title={isConversionDisabled ? conversionEligibility.reason : 'Chuyển đổi dịch vụ sang dịch vụ khác'}
                            placement="left"
                        >
                            <span
                                style={{
                                    cursor: isConversionDisabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block',
                                }}
                            >
                                <span
                                    className={
                                        isConversionDisabled
                                            ? 'disabled-action'
                                            : 'item-action-btn'
                                    }
                                    onClick={
                                        !isConversionDisabled
                                            ? () =>
                                                this.onConvert(
                                                    record.customer_service_id
                                                )
                                            : undefined
                                    }
                                >
                                    <SwapOutlined /> Chuyển đổi
                                </span>
                            </span>
                        </Tooltip>
                    )
                );
            },
        },
    ];

    buildColumnsTransfer = () => {
        return [
            {
                title: 'STT',
                dataIndex: 'stt',
                width: 52,
                align: 'center',
                render: (text, record, index) => index + 1,
            },
            {
                title: 'Mã phiếu',
                dataIndex: 'code',
                key: 'code',
                width: 110,
            },
            {
                title: 'Sản phẩm/dịch vụ',
                dataIndex: 'name',
                width: 310,
                render: (text, record) => {
                    const isTransferred = record.is_transferred;
                    const from = record.converted_from_service_name;
                    const to = record.converted_to_service_name;

                    return (
                        <div>
                            <div
                                style={{
                                    fontSize: 15,
                                    lineHeight: '21px',
                                    fontWeight: 600,
                                    color: 'rgba(0, 0, 0, 0.88)',
                                }}
                            >
                                {text}
                            </div>

                            {(from || (isTransferred && to)) && (
                                <Space
                                    size={[4, 3]}
                                    wrap
                                    style={{ marginTop: 4 }}
                                >
                                    {from && (
                                        <Tooltip
                                            title={`Được chuyển từ: ${from}`}
                                        >
                                            <Tag
                                                color="green"
                                                style={{
                                                    margin: 0,
                                                }}
                                            >
                                                Được chuyển từ: {from}
                                            </Tag>
                                        </Tooltip>
                                    )}

                                    {isTransferred && to && (
                                        <Tooltip
                                            title={`Đã chuyển sang: ${to}`}
                                        >
                                            <Tag
                                                color="red"
                                                style={{
                                                    margin: 0,
                                                }}
                                            >
                                                Đã chuyển sang: {to}
                                            </Tag>
                                        </Tooltip>
                                    )}
                                </Space>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Số lượng',
                dataIndex: 'quantity',
                width: 90,
                align: 'center',
            },
            {
                title: 'Đơn giá',
                dataIndex: 'price',
                width: 140,
                align: 'right',
                render: text => (
                    <NumberFormat
                        value={text}
                        displayType="text"
                        suffix="đ"
                        thousandSeparator
                    />
                ),
            },
            {
                title: 'Thành tiền',
                dataIndex: 'line_total',
                width: 145,
                align: 'right',
                render: (_, record) => (
                    <span style={{ fontWeight: 600 }}>
                        <NumberFormat
                            value={
                                Number(record.price || 0) *
                                Number(record.quantity || 0)
                            }
                            displayType="text"
                            suffix="đ"
                            thousandSeparator
                        />
                    </span>
                ),
            },
            {
                title: 'Số tiền chênh lệch',
                dataIndex: 'amount',
                width: 180,
                align: 'right',
                render: (text, record) => {
                    const { amount_to_pay = 0, amount_to_deduct = 0 } = record;

                    if (amount_to_pay > 0) {
                        return (
                            <div
                                style={{
                                    color: '#237804',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                }}
                            >
                                +{' '}
                                <NumberFormat
                                    value={amount_to_pay}
                                    displayType="text"
                                    thousandSeparator
                                    suffix="đ"
                                />
                                <div
                                    style={{
                                        marginTop: 1,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    Khách thanh toán thêm
                                </div>
                            </div>
                        );
                    }

                    if (amount_to_deduct > 0) {
                        return (
                            <div
                                style={{
                                    color: '#cf1322',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                }}
                            >
                                -{' '}
                                <NumberFormat
                                    value={amount_to_deduct}
                                    displayType="text"
                                    thousandSeparator
                                    suffix="đ"
                                />
                                <div
                                    style={{
                                        marginTop: 1,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    Hoàn lại cho khách
                                </div>
                            </div>
                        );
                    }

                    return (
                        <span
                            style={{
                                color: 'rgba(0, 0, 0, 0.35)',
                            }}
                        >
                            Không chênh lệch
                        </span>
                    );
                },
            },
        ];
    };

    buildFinancialTimelineColumns = () => {
        return [
            {
                title: 'Thời gian',
                dataIndex: 'event_at',
                width: 160,
                render: value =>
                    value ? moment(value).format('DD/MM/YYYY HH:mm') : '—',
            },
            {
                title: 'Loại biến động',
                dataIndex: 'event_label',
                width: 220,
                render: value => (
                    <Tag
                        style={{
                            margin: 0,
                            fontSize: 13,
                        }}
                    >
                        {value}
                    </Tag>
                ),
            },
            {
                title: 'Số tiền',
                dataIndex: 'signed_amount',
                width: 155,
                align: 'right',
                render: value => {
                    const amount = Number(value) || 0;
                    const isNegative = amount < 0;

                    return (
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: isNegative ? '#cf1322' : '#237804',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {`${isNegative ? '-' : '+'}${renderMoney(
                                Math.abs(amount)
                            )}`}
                        </span>
                    );
                },
            },
            {
                title: 'Dịch vụ liên quan',
                dataIndex: 'service_name',
                width: 230,
                render: value =>
                    typeof value === 'string' && value ? value : '—',
            },
            {
                title: 'Người thực hiện',
                dataIndex: 'actor_name',
                width: 175,
                render: value =>
                    typeof value === 'string' && value ? value : 'Hệ thống',
            },
            {
                title: 'Nội dung',
                dataIndex: 'reason',
                width: 280,
                render: value => (typeof value === 'string' ? value : '—'),
            },
        ];
    };

    ensurePrintRendered = () => {
        if (!this.state.shouldRenderPrint) {
            this.setState({
                shouldRenderPrint: true,
            });
        }
    };

    render() {
        const {
            isOpenForm,
            currentService,
            isOpenConvertForm,
            shouldRenderPrint,
        } = this.state;

        const { visible, currentReceipt, loadingDetail } = this.props;

        const customer = this.buildCustomer(currentReceipt);
        const financialTimeline = this.buildFinancialTimeline(
            currentReceipt?.financial_lineage
        );

        const refundedAmount = this.getRefundedAmount(
            currentReceipt?.financial_lineage
        );
        const refundSummary = currentReceipt?.refund_summary || {
            status: refundedAmount > 0 && refundedAmount >= Number(currentReceipt?.amount_paid || 0)
                ? 'FULL'
                : refundedAmount > 0 ? 'PARTIAL' : 'NONE',
            receipt_amount: Number(currentReceipt?.amount_paid || 0),
            refunded_amount: refundedAmount,
            remaining_amount: Math.max(0, Number(currentReceipt?.amount_paid || 0) - refundedAmount),
            history: [],
        };
        const hasRefund = refundSummary.status !== 'NONE';
        const isFullyRefunded = refundSummary.status === 'FULL';
        const canUpdateFinancial = refundSummary.can_update_financial !== false;
        const refundableAmount = Math.max(0, Number(
            refundSummary.refundable_amount ?? refundSummary.remaining_amount ?? 0
        ));
        const refundStatusMessage = refundSummary.refund_status_message
            || `Đã hoàn ${renderMoney(refundSummary.refunded_amount)} / ${renderMoney(refundSummary.receipt_amount)}`;
        const financialUpdateMessage = refundSummary.financial_update_message
            || (canUpdateFinancial ? null : 'Chỉ có thể sửa ghi chú và hình ảnh.');
        const allocationRows = this.buildAllocationRows(
            currentReceipt?.financial_lineage
        );
        const contentRows = buildReceiptContentRows(
            currentReceipt?.items,
            allocationRows,
            currentReceipt?.service_allocation_summary
        );
        //console.log('contentRows', contentRows);

        const contentSummary = buildReceiptContentSummary(contentRows);
        const settlementSummary = currentReceipt?.settlement_summary || {};
        const productSettlementAmount = Number(
            settlementSummary.product_settlement_amount ?? currentReceipt?.product_amount ?? 0
        );
        const serviceSettlementAmount = Number(
            settlementSummary.service_settlement_amount ?? currentReceipt?.service_amount_paid ?? 0
        );
        const balanceUsed = Number(settlementSummary.balance_amount ?? this.getAllocationSourceTotal(
            allocationRows,
            'CUSTOMER_BALANCE'
        ));
        const depositUsed = Number(settlementSummary.deposit_amount ?? this.getAllocationSourceTotal(
            allocationRows,
            'CUSTOMER_DEPOSIT'
        ));
        const isDepositReceipt =
            currentReceipt?.collection_purpose === 'DEPOSIT';
        const depositInfo = currentReceipt?.deposit_info || {};
        const depositContent = isDepositReceipt
            ? getReceiptCurrentContent(currentReceipt)
            : '';
        const displayedDepositRefunded = isDepositReceipt
            ? Math.max(Number(depositInfo.refunded_amount || 0), Number(refundSummary.refunded_amount || 0))
            : 0;
        const displayedDepositBalance = isDepositReceipt
            ? Number(refundSummary.refundable_deposit_amount ?? depositInfo.current_balance ?? 0)
            : 0;
        const depositCurrentDescription = isDepositReceipt && depositInfo.deposit_id ? <div>
            <div>
                Tiền thực thu: <strong>{renderMoney(depositInfo.received_amount || 0)}</strong>
                {' · '}Ban đầu: <strong>{renderMoney(depositInfo.initial_received_amount || depositInfo.received_amount || 0)}</strong>
            </div>
            {(Number(depositInfo.receipt_update_in_amount || 0) > 0 || Number(depositInfo.receipt_update_out_amount || 0) > 0) && <div>
                Cập nhật tăng: <strong>{renderMoney(depositInfo.receipt_update_in_amount || 0)}</strong>
                {' · '}Cập nhật giảm: <strong>{renderMoney(depositInfo.receipt_update_out_amount || 0)}</strong>
            </div>}
            <div>
                Đã sử dụng: <strong>{renderMoney(depositInfo.applied_amount || 0)}</strong>
                {' · '}Đã hoàn: <strong>{renderMoney(displayedDepositRefunded)}</strong>
            </div>
            {(Number(depositInfo.transferred_in_amount || 0) > 0 || Number(depositInfo.transferred_out_amount || 0) > 0) && <div>
                Nhận chuyển: <strong>{renderMoney(depositInfo.transferred_in_amount || 0)}</strong>
                {' · '}Đã chuyển: <strong>{renderMoney(depositInfo.transferred_out_amount || 0)}</strong>
            </div>}
            <div>
                Số dư cọc: <strong>{renderMoney(displayedDepositBalance)}</strong>
                {' · '}Trạng thái: <strong>{getDepositStatusLabel(depositInfo.status)}</strong>
            </div>
            <div>Nội dung lúc nhận: {formatDepositDisplayText(depositInfo.initial_content, 'Chưa có nội dung')}</div>
        </div> : null;
        const canUpdateDeposit = isDepositReceipt
            && !!depositInfo.deposit_id
            && depositInfo.status !== 'CANCELLED'
            && currentReceipt?.can_update_deposit === true
            && checkPermission('receipt', 'update')
            && checkPermission('customer_deposit', 'view');
        const currentDebt = Math.max(
            0,
            Number(currentReceipt?.customer_current_finance?.debt_amount) || 0
        );

        const debtAtReceipt = Math.max(
            0,
            Number(currentReceipt?.remaining_debt) || 0
        );
        const receiptNote =
            typeof currentReceipt?.note === 'string'
                ? currentReceipt.note.trim()
                : '';
        const receiptImages = Array.isArray(currentReceipt?.images)
            ? currentReceipt.images
            : [];
        const hasRelatedDetails =
            financialTimeline.length > 0 ||
            (currentReceipt?.transfer_items || []).length > 0;

        const sectionCardStyle = {
            marginBottom: 10,
            borderRadius: 10,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        };

        const cardBodyStyle = {
            padding: isMobile ? 10 : '11px 14px',
        };

        return (
            <>
                <style>
                    {`
                        .receipt-detail-v2 .ant-table {
                            font-size: 14px;
                        }

                        .receipt-detail-v2 .ant-table-thead > tr > th {
                            padding: 9px 10px !important;
                            font-size: 13px;
                            font-weight: 700;
                            color: rgba(0, 0, 0, 0.65);
                            background: #fafafa;
                            white-space: nowrap;
                        }

                        .receipt-detail-v2 .ant-table-tbody > tr > td {
                            padding: 8px 10px !important;
                        }

                        .receipt-detail-v2 .ant-collapse {
                            border-radius: 8px;
                            overflow: hidden;
                            background: #fff;
                        }

                        .receipt-detail-v2 .ant-collapse > .ant-collapse-item > .ant-collapse-header {
                            padding: 10px 12px !important;
                            font-size: 15px;
                            font-weight: 600;
                        }

                        .receipt-detail-v2 .ant-collapse-content > .ant-collapse-content-box {
                            padding: 10px 12px !important;
                        }

                        .receipt-detail-v2 .ant-card-body {
                            overflow: hidden;
                        }

                        @media (max-width: 767px) {
                            .receipt-detail-v2 .ant-table {
                                font-size: 13px;
                            }

                            .receipt-detail-v2 .ant-table-thead > tr > th,
                            .receipt-detail-v2 .ant-table-tbody > tr > td {
                                padding: 7px 8px !important;
                            }
                        }
                    `}
                </style>

                <Drawer
                    visible={visible}
                    title={`Chi tiết phiếu thu: #${currentReceipt?.id || ''}`}
                    onClose={this.handleCancel}
                    maskClosable={false}
                    width={isMobile ? '100%' : '82%'}
                    bodyStyle={{
                        padding: isMobile ? 8 : 12,
                        background: '#f5f7fa',
                    }}
                    extra={
                        <Space
                            size={6}
                            style={{
                                flexWrap: 'nowrap',
                            }}
                        >
                            <ReceiptHistoryButton onClick={this.openHistory} />
                            {canUpdateDeposit ? (
                                <Button
                                    type="primary"
                                    loading={this.state.depositUpdateLoading}
                                    onClick={this.openDepositUpdate}
                                    icon={<EditOutlined />}
                                >
                                    Cập nhật phiếu cọc
                                </Button>
                            ) : null}
                            {checkPermission('receipt', 'update') &&
                                currentReceipt?.collection_purpose !== 'DEPOSIT' &&
                                canUpdateFinancial ? (
                                <Button
                                    type="primary"
                                    onClick={() => this.toggleOpenForm(true)}
                                    icon={<EditOutlined />}
                                >
                                    Sửa phiếu thu
                                </Button>
                            ) : null}

                            {checkPermission('customer_refund', 'execute') &&
                                refundableAmount > 0 ? (
                                <Button
                                    danger
                                    onClick={() => this.toggleOpenRefund(true)}
                                    icon={<UndoOutlined />}
                                >
                                    Hoàn tiền
                                </Button>
                            ) : null}

                            {/* Lazy Print: chỉ mount PrintTable khi user muốn in */}
                            <div
                                onMouseEnter={this.ensurePrintRendered}
                                onFocus={this.ensurePrintRendered}
                            >
                                <PrintButton>
                                    {shouldRenderPrint ? (
                                        <PrintTable
                                            customer={customer}
                                            tableData={
                                                currentReceipt?.items || []
                                            }
                                        />
                                    ) : (
                                        <span />
                                    )}
                                </PrintButton>
                            </div>
                        </Space>
                    }
                >
                    {visible ? (
                        <Spin tip="Loading..." spinning={loadingDetail}>
                            <div className="wrap-customer-detail receipt-detail-v2">
                                {/* 1. NHẬN DIỆN PHIẾU THU */}
                                <Card
                                    bordered={false}
                                    style={sectionCardStyle}
                                    bodyStyle={cardBodyStyle}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 8,
                                            marginBottom: 6,
                                        }}
                                    >
                                        <div
                                            style={{
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginBottom: 1,
                                                    fontSize: 13,
                                                    lineHeight: '19px',
                                                    color:
                                                        'rgba(0, 0, 0, 0.45)',
                                                }}
                                            >
                                                Khách hàng
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: isMobile
                                                        ? 20
                                                        : 22,
                                                    lineHeight: '30px',
                                                    fontWeight: 700,
                                                    color:
                                                        'rgba(0, 0, 0, 0.88)',
                                                }}
                                            >
                                                {currentReceipt?.customer
                                                    ?.full_name || '-'}
                                            </div>

                                            <Space
                                                size={[5, 4]}
                                                wrap
                                                style={{
                                                    marginTop: 4,
                                                }}
                                            >
                                                {currentReceipt?.customer
                                                    ?.code ? (
                                                    <Paragraph
                                                        style={{
                                                            margin: 0,
                                                        }}
                                                        copyable={{
                                                            text:
                                                                currentReceipt
                                                                    ?.customer
                                                                    ?.code,
                                                            tooltips: false,
                                                        }}
                                                    >
                                                        <Tag
                                                            style={{
                                                                margin: 0,
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            Mã KH:{' '}
                                                            {
                                                                currentReceipt
                                                                    ?.customer
                                                                    ?.code
                                                            }
                                                        </Tag>
                                                    </Paragraph>
                                                ) : null}

                                                {currentReceipt?.code && (
                                                    <Tag
                                                        color="blue"
                                                        style={{
                                                            margin: 0,
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        Mã phiếu:{' '}
                                                        {currentReceipt.code}
                                                    </Tag>
                                                )}

                                                {currentReceipt?.receipt_type === 'service_conversion' &&
                                                    ConvertReceiptType(currentReceipt.receipt_type)}
                                            </Space>
                                        </div>

                                        <div
                                            style={{
                                                flexShrink: 0,
                                                textAlign: isMobile
                                                    ? 'left'
                                                    : 'right',
                                            }}
                                        >
                                            {renderReceiptStatus(
                                                currentReceipt?.status
                                            )}
                                        </div>
                                    </div>
                                </Card>

                                {hasRefund && (
                                    <Alert
                                        type={canUpdateFinancial ? 'warning' : 'error'}
                                        style={{ marginBottom: 10 }}
                                        message={refundStatusMessage}
                                        description={!isFullyRefunded || financialUpdateMessage ? (
                                            <div>
                                                {!isFullyRefunded && (
                                                    <div>Còn có thể hoàn: <strong>{renderMoney(refundableAmount)}</strong></div>
                                                )}
                                                {financialUpdateMessage && <div>{financialUpdateMessage}</div>}
                                            </div>
                                        ) : null}
                                    />
                                )}

                                {/* 2. TỔNG QUAN TÀI CHÍNH - ƯU TIÊN CAO NHẤT */}
                                <Card
                                    bordered={false}
                                    style={sectionCardStyle}
                                    bodyStyle={cardBodyStyle}
                                >
                                    {this.renderSectionHeader(
                                        'Tổng quan tài chính',
                                        'Các số liệu cần kiểm tra nhanh trên phiếu thu'
                                    )}

                                    <Row gutter={[8, 8]} align="stretch">
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderFinancialMetric(
                                                'Doanh số',
                                                renderMoney(
                                                    currentReceipt?.adjusted_sales
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderFinancialMetric(
                                                'Khách thực nộp mới',
                                                renderMoney(
                                                    currentReceipt?.amount_paid
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col>
                                        {!isDepositReceipt && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Tổng thanh toán lần này',
                                                    renderMoney(settlementSummary.settlement_total ?? currentReceipt?.amount_paid),
                                                    { primary: true }
                                                )}
                                            </Col>
                                        )}
                                        {!isDepositReceipt && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Khấu trừ công nợ thực tế',
                                                    renderMoney(settlementSummary.debt_reduction_amount || 0),
                                                    { primary: true }
                                                )}
                                            </Col>
                                        )}
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderFinancialMetric(
                                                'Tổng đã thu',
                                                renderMoney(
                                                    currentReceipt?.total_amount_paid
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col>
                                        {refundSummary.refunded_amount > 0 && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Tổng tiền đã hoàn',
                                                    renderMoney(refundSummary.refunded_amount),
                                                    { danger: hasRefund }
                                                )}
                                            </Col>
                                        )}

                                        <Col xs={24} sm={12} lg={8} xl={6}>
                                            {this.renderFinancialMetric(
                                                'Công nợ hiện tại',
                                                renderMoney(currentDebt),
                                                {
                                                    primary: true,
                                                    danger: currentDebt > 0,
                                                }
                                            )}
                                        </Col>
                                        {refundSummary.refunded_amount > 0 && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Trạng thái hoàn tiền',
                                                    isFullyRefunded ? 'Đã hoàn toàn bộ' : hasRefund ? 'Đã hoàn một phần' : 'Chưa hoàn tiền',
                                                    { danger: hasRefund, strong: true }
                                                )}
                                            </Col>
                                        )}

                                    </Row>
                                    {debtAtReceipt !== currentDebt && (
                                        <div
                                            style={{
                                                marginTop: 10,
                                                color: 'rgba(0, 0, 0, 0.65)',
                                            }}
                                        >
                                            Công nợ ngay sau khi thu:{' '}
                                            <strong>
                                                {renderMoney(debtAtReceipt)}
                                            </strong>
                                        </div>
                                    )}
                                </Card>

                                {(contentRows.length > 0 ||
                                    isDepositReceipt ||
                                    receiptNote ||
                                    receiptImages.length > 0) && (
                                        <Card
                                            bordered={false}
                                            style={sectionCardStyle}
                                            bodyStyle={cardBodyStyle}
                                        >
                                            {this.renderSectionHeader(
                                                'Dịch vụ và nội dung thu',
                                                'Chi tiết sản phẩm, dịch vụ và nội dung ghi trên phiếu'
                                            )}

                                            {isDepositReceipt && (
                                                <Alert
                                                    type="info"
                                                    message={depositContent}
                                                    description={depositCurrentDescription}
                                                    style={{ marginBottom: 12 }}
                                                />
                                            )}

                                            {contentRows?.length ? (
                                                <Table
                                                    size="small"
                                                    rowKey={this.rowKeyItems}
                                                    tableLayout="fixed"
                                                    columns={this.contentColumns}
                                                    dataSource={contentRows}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                />
                                            ) : !isDepositReceipt ? (
                                                <Empty
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    description="Không có nội dung"
                                                />
                                            ) : null}

                                            {(receiptNote ||
                                                receiptImages.length > 0) && (
                                                    <div
                                                        style={{
                                                            marginTop: contentRows.length
                                                                ? 12
                                                                : 0,
                                                            paddingTop: contentRows.length
                                                                ? 12
                                                                : 0,
                                                            borderTop: contentRows.length
                                                                ? '1px solid #f0f0f0'
                                                                : 'none',
                                                        }}
                                                    >
                                                        {receiptNote && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        marginBottom: 4,
                                                                        color:
                                                                            'rgba(0, 0, 0, 0.45)',
                                                                    }}
                                                                >
                                                                    Ghi chú
                                                                </div>
                                                                <div>
                                                                    {ReactHtmlParser(
                                                                        receiptNote
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {receiptNote &&
                                                            receiptImages.length >
                                                            0 && (
                                                                <Divider
                                                                    style={{
                                                                        margin:
                                                                            '10px 0',
                                                                    }}
                                                                />
                                                            )}

                                                        {!!receiptImages.length && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        marginBottom: 6,
                                                                        color:
                                                                            'rgba(0, 0, 0, 0.45)',
                                                                    }}
                                                                >
                                                                    Ảnh phiếu
                                                                </div>
                                                                <ImagePreviewList
                                                                    images={
                                                                        receiptImages
                                                                    }
                                                                    max={10}
                                                                    thumbSize={60}
                                                                    initialShowAll={
                                                                        false
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                        </Card>
                                    )}

                                {(balanceUsed > 0 ||
                                    (!isDepositReceipt && depositUsed > 0)) && (
                                        <Card
                                            bordered={false}
                                            style={sectionCardStyle}
                                            bodyStyle={cardBodyStyle}
                                        >
                                            {this.renderSectionHeader(
                                                'Số dư/cọc đã dùng',
                                                'Các nguồn tiền có sẵn của khách được dùng cùng phiếu này'
                                            )}
                                            <Row gutter={[8, 8]}>
                                                {balanceUsed > 0 && (
                                                    <Col xs={24} sm={12}>
                                                        {this.renderFinancialMetric(
                                                            'Số dư khách hàng đã dùng',
                                                            renderMoney(
                                                                balanceUsed
                                                            ),
                                                            { positive: true }
                                                        )}
                                                    </Col>
                                                )}
                                                {!isDepositReceipt &&
                                                    depositUsed > 0 && (
                                                        <Col xs={24} sm={12}>
                                                            {this.renderFinancialMetric(
                                                                'Cọc đã dùng',
                                                                renderMoney(
                                                                    depositUsed
                                                                ),
                                                                { positive: true }
                                                            )}
                                                        </Col>
                                                    )}
                                            </Row>
                                        </Card>
                                    )}

                                {hasRefund && (
                                    <Card
                                        bordered={false}
                                        style={sectionCardStyle}
                                        bodyStyle={cardBodyStyle}
                                    >
                                        {this.renderSectionHeader(
                                            'Lịch sử hoàn tiền'
                                        )}
                                        {(refundSummary.history || []).map(item => (
                                            <div key={item.refund_id} style={{ marginTop: 8 }}>
                                                <strong>{isValidDate(item.refund_at, true)}</strong>
                                                {' · '}{renderMoney(item.amount)}
                                                {item.actor_name ? ` · ${item.actor_name}` : ''}
                                                {item.reason ? <div style={{ color: 'rgba(0, 0, 0, 0.65)' }}>{item.reason}</div> : null}
                                            </div>
                                        ))}
                                    </Card>
                                )}

                                {/* 4. THÔNG TIN THU */}
                                <Card
                                    bordered={false}
                                    style={sectionCardStyle}
                                    bodyStyle={cardBodyStyle}
                                >
                                    {this.renderSectionHeader(
                                        'Thông tin phiếu thu',
                                        'Ngày thu, hình thức và thông tin ghi nhận'
                                    )}

                                    <Row gutter={[8, 8]} align="stretch">
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderInfoField(
                                                'Ngày thu',
                                                isValidDate(
                                                    currentReceipt?.payment_at,
                                                    false
                                                ),
                                                {
                                                    icon: <CalendarOutlined />,
                                                    primary: true,
                                                }
                                            )}
                                        </Col>

                                        <Col xs={24} sm={12} lg={8}>
                                            {this.renderInfoField(
                                                'Hình thức thu',
                                                (
                                                    currentReceipt?.payment_breakdowns ||
                                                    currentReceipt?.paymentBreakdowns ||
                                                    []
                                                ).length
                                                    ? (
                                                        currentReceipt.payment_breakdowns ||
                                                        currentReceipt.paymentBreakdowns
                                                    ).map(row => (
                                                        <div
                                                            key={
                                                                row.id ||
                                                                row.payment_method
                                                            }
                                                        >
                                                            {renderPaymentMethod(
                                                                row.payment_method
                                                            )}{' '}
                                                            <strong>
                                                                {renderMoney(
                                                                    row.amount
                                                                )}
                                                            </strong>
                                                        </div>
                                                    ))
                                                    : renderPaymentMethod(
                                                        currentReceipt?.payment_method
                                                    ),
                                                {
                                                    icon: <CreditCardOutlined />,
                                                    surface: true,
                                                }
                                            )}
                                        </Col>

                                        <Col xs={12} sm={12} lg={5}>
                                            {this.renderInfoField(
                                                'Nghiệp vụ thu',
                                                getReceiptCollectionPurposeLabel(
                                                    currentReceipt?.collection_purpose
                                                ),
                                                {
                                                    icon: <FileTextOutlined />,
                                                    surface: true,
                                                }
                                            )}
                                        </Col>

                                        <Col xs={12} sm={12} lg={5}>
                                            {this.renderInfoField(
                                                'Lần thanh toán',
                                                currentReceipt?.collection_purpose === 'PAYMENT'
                                                    ? (renderReceiptPaymentType(
                                                        currentReceipt?.payment_type
                                                    ) || '—')
                                                    : '—',
                                                { surface: true }
                                            )}
                                        </Col>
                                    </Row>

                                    <div
                                        style={{
                                            marginTop: 10,
                                            paddingTop: 8,
                                            borderTop: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Row gutter={[16, 2]} align="top">
                                            <Col xs={12} sm={12} lg={6}>
                                                {this.renderInfoField(
                                                    'Cơ sở thu tiền',
                                                    currentReceipt?.branch?.name ||
                                                    '—',
                                                    {
                                                        icon: <BankOutlined />,
                                                        compact: true,
                                                    }
                                                )}
                                            </Col>

                                            {currentReceipt?.commission_processing?.status && (
                                                <Col xs={12} sm={12} lg={6}>
                                                    {this.renderInfoField(
                                                        'Trạng thái hoa hồng',
                                                        this.renderCommissionProcessingStatus(
                                                            currentReceipt.commission_processing.status
                                                        ),
                                                        { compact: true }
                                                    )}
                                                </Col>
                                            )}

                                            {currentReceipt?.paper_receipt_number && (
                                                <Col xs={12} sm={12} lg={6}>
                                                    {this.renderInfoField(
                                                        'Số phiếu giấy',
                                                        currentReceipt.paper_receipt_number,
                                                        { compact: true }
                                                    )}
                                                </Col>
                                            )}

                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderInfoField(
                                                    'Lập phiếu',
                                                    <CreatedAtCell
                                                        createdAt={
                                                            currentReceipt?.created_at
                                                        }
                                                        user={
                                                            currentReceipt?.created_user
                                                        }
                                                        layout="horizontal"
                                                    />,
                                                    {
                                                        icon: <UserOutlined />,
                                                        compact: true,
                                                    }
                                                )}
                                            </Col>
                                        </Row>
                                    </div>

                                    {(settlementSummary.product_settlement_amount !== undefined
                                        || settlementSummary.service_settlement_amount !== undefined
                                        || currentReceipt?.product_amount !== undefined
                                        || currentReceipt?.service_amount_paid !== undefined) ? (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                paddingTop: 10,
                                                borderTop: '1px solid #f0f0f0',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginBottom: 6,
                                                    fontSize: 13,
                                                    lineHeight: '19px',
                                                    fontWeight: 600,
                                                    color: 'rgba(0, 0, 0, 0.65)',
                                                }}
                                            >
                                                Phân bổ tổng thanh toán
                                            </div>
                                            <Row gutter={[8, 8]} align="stretch">
                                                {(settlementSummary.product_settlement_amount !== undefined
                                                    || currentReceipt?.product_amount !== undefined) && (
                                                        <Col xs={24} sm={12}>
                                                            {this.renderFinancialMetric(
                                                                <Tooltip title="Tổng giá trị sản phẩm đã thanh toán trong phiếu từ tiền khách thực nộp, tiền cọc và số dư. Mỗi nguồn được lưu riêng để không cộng trùng doanh thu và có thể hoàn nguyên khi sửa/hủy.">
                                                                    <span>
                                                                        Thanh toán sản phẩm{' '}
                                                                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                                                    </span>
                                                                </Tooltip>,
                                                                renderMoney(productSettlementAmount),
                                                                { primary: true }
                                                            )}
                                                        </Col>
                                                    )}
                                                {(settlementSummary.service_settlement_amount !== undefined
                                                    || currentReceipt?.service_amount_paid !== undefined) && (
                                                        <Col xs={24} sm={12}>
                                                            {this.renderFinancialMetric(
                                                                <Tooltip title="Tổng giá trị đã phân bổ cho dịch vụ trong phiếu từ tiền khách thực nộp, tiền cọc và số dư; không bao gồm phần thanh toán sản phẩm.">
                                                                    <span>
                                                                        Tiền dành cho dịch vụ{' '}
                                                                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                                                    </span>
                                                                </Tooltip>,
                                                                renderMoney(serviceSettlementAmount),
                                                                { primary: true }
                                                            )}
                                                        </Col>
                                                    )}
                                            </Row>
                                        </div>
                                    ) : null}
                                </Card>

                                {/* 5. THÔNG TIN PHỤ / LỊCH SỬ DÀI */}
                                {hasRelatedDetails && (
                                    <Collapse
                                        bordered
                                        defaultActiveKey={[]}
                                        style={{
                                            marginBottom: 10,
                                            background: '#fff',
                                        }}
                                    >
                                        {!!financialTimeline.length && (
                                            <Panel
                                                header={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 8,
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <span>
                                                            Lịch sử số dư từ
                                                            phiếu này
                                                        </span>
                                                        <Tag
                                                            style={{
                                                                margin: 0,
                                                            }}
                                                        >
                                                            {
                                                                financialTimeline.length
                                                            }{' '}
                                                            biến động
                                                        </Tag>
                                                    </div>
                                                }
                                                key="financial-history"
                                            >
                                                <Table
                                                    size="small"
                                                    pagination={{
                                                        pageSize: 10,
                                                    }}
                                                    rowKey="timeline_key"
                                                    tableLayout="fixed"
                                                    dataSource={
                                                        financialTimeline
                                                    }
                                                    columns={
                                                        this
                                                            .financialTimelineColumns
                                                    }
                                                    scroll={{
                                                        x: 1220,
                                                    }}
                                                />
                                            </Panel>
                                        )}

                                        {!!currentReceipt?.transfer_items
                                            ?.length && (
                                                <Panel
                                                    header={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 8,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            <span>
                                                                Phiếu chuyển đổi
                                                                dịch vụ
                                                            </span>
                                                            <Tag
                                                                style={{
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {
                                                                    currentReceipt
                                                                        .transfer_items
                                                                        .length
                                                                }{' '}
                                                                mục
                                                            </Tag>
                                                        </div>
                                                    }
                                                    key="transfer-items"
                                                >
                                                    <Table
                                                        size="small"
                                                        rowKey={
                                                            this.rowKeyTransferItems
                                                        }
                                                        tableLayout="fixed"
                                                        columns={
                                                            this.columnsTransfer
                                                        }
                                                        locale={{
                                                            emptyText:
                                                                'Chưa có dữ liệu',
                                                        }}
                                                        dataSource={
                                                            currentReceipt?.transfer_items ||
                                                            []
                                                        }
                                                        pagination={false}
                                                        scroll={{
                                                            x: 1027,
                                                        }}
                                                    />
                                                </Panel>
                                            )}
                                    </Collapse>
                                )}
                            </div>
                        </Spin>
                    ) : null}
                </Drawer>

                <ReceiptForm
                    visible={isOpenForm}
                    currentReceipt={currentReceipt}
                    reloadData={this.loadData}
                    onCancel={() => this.toggleOpenForm(false)}
                />

                {this.state.isOpenDepositUpdate && this.state.depositUpdateData ? (
                    <CustomerDepositActions
                        customerId={currentReceipt?.customer_id}
                        data={this.state.depositUpdateData}
                        reload={this.loadData}
                        autoOpenUpdate
                        initialDeposit={this.state.depositToUpdate}
                        showActions={false}
                        onClose={this.closeDepositUpdate}
                    />
                ) : null}

                <CustomerServiceConvertForm
                    visible={isOpenConvertForm}
                    currentService={currentService}
                    currentReceipt={currentReceipt}
                    reloadData={this.loadData}
                    onCancel={() => this.toggleOpenConvertForm(false)}
                />

                <CustomerRefundModal
                    visible={this.state.isOpenRefund}
                    customerId={currentReceipt?.customer_id}
                    receiptId={currentReceipt?.id}
                    receiptCode={currentReceipt?.code}
                    onCancel={() => this.toggleOpenRefund(false)}
                    onCompleted={this.loadData}
                />
                <ReceiptHistoryDrawer
                    visible={this.state.isOpenHistory}
                    loading={this.state.historyLoading}
                    items={this.state.historyItems}
                    onClose={() => this.setState({ isOpenHistory: false })}
                />
            </>
        );
    }
}

function mapStateToProps(state) {
    return {
        authUser: state.auth.authUser,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        getSpecificService: id => dispatch(getSpecificService(id)),
        getReceiptHistory: id => dispatch(getReceiptHistory(id)),
        getCustomerDepositSummary: customerId => dispatch(getCustomerDepositSummary(customerId)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Form.create({ name: 'ReceiptDetail' })(ReceiptDetail));
